import {
  ScoreTier,
  ScoreStyleClasses,
  RatingScoreData,
  FlightRiskFormState,
  FlightRiskApiResponse,
  EditableFlightRiskFields,
  EditableFlightRiskFieldKey,
  UpdateFlightRiskFieldsPayload,
  EDITABLE_FLIGHT_RISK_FIELDS,
} from "../interfaces/flightRisk";

export type { ScoreTier, ScoreStyleClasses, RatingScoreData };

// ─── Initial Empty Form State ──────────────────────────────────────────────────
export const EMPTY_FORM: FlightRiskFormState = {
  email: "",
  hubLeader: "",
  strategist: "",
  ppcManager: "",
  urgency: "",
  confidence: "",
  walmartAm: "",
  mainIssue: "",
  contributingFactor: "",
  kpi: "",
  sales: "",
  roas: "",
  poaInPlace: "",
  poaRecap: "",
  dateOfLastInteraction: "",
  goalsMovingForward: "",
  peopleInvolved: "",
};

/** Convert API response data to local form state */
export const apiToForm = (d: FlightRiskApiResponse | any): FlightRiskFormState => ({
  email: d?.emailAddress?.trim() || "",
  hubLeader: d?.hubLeaderEmail?.trim() || "",
  strategist: d?.strategistEmail?.trim() || "",
  ppcManager: d?.ppcManagerEmail?.trim() || "",
  urgency: d?.levelOfUrgencyForThisFlightRisk?.trim() ?? "",
  confidence:
    d?.onAScaleOf1To5HowConfidentAreYouInRetainingThisClientWithTheCurrentPlanOfActionPoa?.trim() ??
    "",
  walmartAm: d?.doesTheAccountHaveAWalmartAmOrConnectPartner?.trim() ?? "",
  mainIssue: d?.mainIssueReasonForFlightRiskFlag?.trim() ?? "",
  contributingFactor:
    d?.whichOfTheFollowingFactorsPrimarilyContributedToTheFlightRisk?.trim() ?? "",
  kpi: Array.isArray(d?.kpiThatBestRepresentsTheMainIssue)
    ? d.kpiThatBestRepresentsTheMainIssue
        .filter((k: string) => typeof k === "string" && k.trim() !== "")
        .join(", ")
    : (typeof d?.kpiThatBestRepresentsTheMainIssue === "string"
      ? d.kpiThatBestRepresentsTheMainIssue.trim()
      : ""),
  sales: d?.last30DaysOfSales != null ? String(d.last30DaysOfSales) : "",
  roas:
    d?.last30DaysReturnOnAdSpendRoas != null
      ? String(d.last30DaysReturnOnAdSpendRoas)
      : "",
  poaInPlace: d?.isAPlanOfActionPoaInPlace?.trim() ?? "",
  poaRecap:
    typeof d?.recapOfPlanOfActionPoaDetailsAndNextStepsIfApplicable === "string"
      ? d.recapOfPlanOfActionPoaDetailsAndNextStepsIfApplicable
      : Array.isArray(d?.recapOfPlanOfActionPoaDetailsAndNextStepsIfApplicable)
        ? d.recapOfPlanOfActionPoaDetailsAndNextStepsIfApplicable.join("\n")
        : "",
  dateOfLastInteraction: d?.dateOfLastSignificantInteraction
    ? d.dateOfLastSignificantInteraction.split("T")[0].split(" ")[0].trim()
    : "",
  goalsMovingForward: d?.goalsMovingForward?.trim() ?? "",
  peopleInvolved: d?.peopleInvolved?.trim() ?? "",
});

/** Validates that an urgency or confidence rating is an integer between 1 and 5 */
export const validateScore = (val: string): string | undefined => {
  if (val.trim() === "") return undefined;
  const n = Number(val);
  if (!Number.isInteger(n) || isNaN(n) || n < 1 || n > 5) {
    return "Must be between 1 and 5";
  }
  return undefined;
};


/** Deep comparison of two form states */
export const formsAreEqual = (
  a: FlightRiskFormState,
  b: FlightRiskFormState,
): boolean => JSON.stringify(a) === JSON.stringify(b);


/**
 * Parses raw rating string and detects if it is in the valid 1–5 range or > 5.
 * Values > 5 will not render dots and will display as raw value in black color.
 */
export const parseRatingScore = (val: string | null): RatingScoreData => {
  if (val === null || val === undefined || val.trim() === "") {
    return { rawValue: null, score: null, isOverMax: false, isValidRating: false };
  }
  const rawValue = val.trim();
  const parsed = parseInt(rawValue, 10);
  if (isNaN(parsed)) {
    return { rawValue, score: null, isOverMax: false, isValidRating: false };
  }
  return {
    rawValue,
    score: parsed,
    isOverMax: parsed > 5,
    isValidRating: parsed >= 1 && parsed <= 5,
  };
};

// ─── Helper: parse string level "1"–"5" to number ─────────────────────────────
export const parseScoreLevel = (val: string | null): number => {
  const parsedNumber = parseInt(val ?? "0", 10);
  return isNaN(parsedNumber) ? 0 : Math.max(0, Math.min(5, parsedNumber));
};

// ─── Urgency: 3 score states (4, 5 = high; 2, 3 = medium; 1 or less = low) ───
export const getUrgencyScoreTier = (score: number): ScoreTier => {
  // High: 4, 5
  if (score >= 4) return "high";
  // Medium: 2, 3
  if (score >= 2) return "medium";
  // Low: 1 or less
  return "low";
};

export const getUrgencyStyleClasses = (score: number): ScoreStyleClasses => {
  const tier = getUrgencyScoreTier(score);
  return {
    circleClass: `flight-risk-circle--urgency-${tier}`,
    dotClass: `flight-risk-dot--urgency-${tier}`,
    scoreTextClass: `flight-risk-score-text--urgency-${tier}`,
  };
};

// ─── Confidence: 3 score states (4, 5 = high; 2, 3 = medium; 1 or less = low) ─
export const getConfidenceScoreTier = (score: number): ScoreTier => {
  // High: 4, 5
  if (score >= 4) return "high";
  // Medium: 2, 3
  if (score >= 2) return "medium";
  // Low: 1 or less
  return "low";
};

export const getConfidenceStyleClasses = (score: number): ScoreStyleClasses => {
  const tier = getConfidenceScoreTier(score);
  return {
    circleClass: `flight-risk-circle--confidence-${tier}`,
    dotClass: `flight-risk-dot--confidence-${tier}`,
    scoreTextClass: `flight-risk-score-text--confidence-${tier}`,
  };
};

// ─── Helper: display Walmart AM field ─────────────────────────────────────────
export const displayWalmartAM = (
  val: string | null
): { text: string; className: string } => {
  if (val === null || val === undefined || val.trim() === "") {
    return { text: "---", className: "flight-risk-fallback-dash" };
  }
  const trimmedVal = val.trim();
  const lowerVal = trimmedVal.toLowerCase();

  if (lowerVal === "no") {
    return { text: "No", className: "flight-risk-walmart-no" };
  }
  if (lowerVal === "yes") {
    return { text: "Yes", className: "flight-risk-walmart-yes" };
  }
  // Any other string (e.g. partner name or custom description)
  return { text: trimmedVal, className: "flight-risk-walmart-other" };
};

export const displayMetricNumber = (
  val: number | string | null | undefined
): { text: string; isNumeric: boolean; isNull: boolean } => {
  if (
    val === null ||
    val === undefined ||
    (typeof val === "string" && val.trim() === "")
  ) {
    return { text: "-", isNumeric: false, isNull: true };
  }
  const strVal = String(val).trim();
  const num = Number(strVal);
  if (isNaN(num)) {
    return { text: strVal, isNumeric: false, isNull: false };
  }
  return {
    text: num.toLocaleString("en-US", { maximumFractionDigits: 3 }),
    isNumeric: true,
    isNull: false,
  };
};

// Backward-compatible aliases
export const parseLevel = parseScoreLevel;
export const getUrgencyColor = (level: number): "red" | "orange" | "gold" => {
  if (level >= 4) return "red";
  if (level >= 2) return "orange";
  return "gold";
};

export const formatDateWithSlash = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  const parts = dateStr.split("T")[0].split(" ")[0].split("-");
  return parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : dateStr;
};

const FIELD_TO_BACKEND_KEY: Record<keyof FlightRiskFormState, EditableFlightRiskFieldKey> = {
  poaRecap: "recap_of_plan_of_action_poa_details_and_next_steps_if_applicable",
  urgency: "level_of_urgency_for_this_flight_risk",
  roas: "last_30_days_return_on_ad_spend_roas",
  kpi: "kpi_that_best_represents_the_main_issue",
  walmartAm: "does_the_account_have_a_walmart_am_or_connect_partner",
  mainIssue: "main_issue_reason_for_flight_risk_flag",
  confidence: "on_a_scale_of_1_to_5_how_confident_are_you_in_retaining_this_client_with_the_current_plan_of_action_poa",
  sales: "last_30_days_of_sales",
  contributingFactor: "which_of_the_following_factors_primarily_contributed_to_the_flight_risk",
  poaInPlace: "is_a_plan_of_action_poa_in_place",
  email: "email_address",
  hubLeader: "hub_leader_email",
  ppcManager: "ppc_manager_email",
  strategist: "strategist_email",
  goalsMovingForward: "goals_moving_forward",
  peopleInvolved: "people_involved",
  dateOfLastInteraction: "date_of_last_significant_interaction",
};


/**
 * Builds the payload for creating or updating a flight risk assessment.
 *
 * Edit mode:
 * - Sends only fields whose values have changed.
 * - Sends "" when an existing value is cleared.
 * - Excludes unchanged fields.
 *
 * Create mode:
 * - Sends all non-empty fields.
 * - Includes the default flight risk status.
 */
export const buildFlightRiskDiffPayload = (
  form: FlightRiskFormState,
  initialForm: FlightRiskFormState | null = null,
  orgId = "",
  subOrgId = "",
  isCreateMode = false,
  flightRiskStatus = "Flagging as a Flight Risk",
): UpdateFlightRiskFieldsPayload => {
  const fieldValues: EditableFlightRiskFields = {};
  const initial = initialForm ?? EMPTY_FORM;

  const normalizeValue = (value: unknown): string =>
    String(value ?? "").trim();

  (
    Object.keys(FIELD_TO_BACKEND_KEY) as Array<keyof FlightRiskFormState>
  ).forEach((key) => {
    const currentValue = normalizeValue(form[key]);
    const initialValue = normalizeValue(initial[key]);

    // Edit mode: skip unchanged fields.
    if (!isCreateMode && currentValue === initialValue) {
      return;
    }

    // Create mode: skip empty fields.
    if (isCreateMode && currentValue === "") {
      return;
    }

    const backendKey = FIELD_TO_BACKEND_KEY[key];
    fieldValues[backendKey] = currentValue;
  });

  // Include the flight risk status and date of last significant interaction when creating.
  if (isCreateMode) {
    fieldValues.flight_risk_status = flightRiskStatus;
  }

  return {
    orgId,
    subOrgId,
    fieldValues,
  };
};

// Backward-compatible alias.
export const formToFlightRiskPayload = buildFlightRiskDiffPayload;

/**
 * Builds the payload for marking a flight risk as resolved.
 * Sets flight_risk_status to "Resolved" and clears all other fields to "".
 */
export const buildResolvedFlightRiskPayload = (
  orgId: string,
  subOrgId: string,
): UpdateFlightRiskFieldsPayload => {
  const fieldValues: EditableFlightRiskFields = {
    flight_risk_status: "Resolved",
  };
  EDITABLE_FLIGHT_RISK_FIELDS.forEach((field) => {
    if (field !== "flight_risk_status") {
      fieldValues[field] = "";
    }
  });
  return {
    orgId,
    subOrgId,
    fieldValues,
  };
};
